import React, { ReactElement } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';

import { ReviewActiveType } from 'modules/review/review.enum';
import { ReviewItemProps } from 'components/review/review.interface';
import ReviewItemMessage from 'components/review/review-item-messages/review-item-message';
import ReviewItemOpiconnect from 'components/review/review-item-opiconnect/review-item-opiconnect';
import ReviewItemProductDesktop from 'components/review/review-item-product/review-item-product.desktop';
import ReviewAddComment from 'components/review/add-comment/review-add-comment';
import ReviewItemLikes from 'components/review/review-item-likes/review-item-likes';
import Rating from 'atomic/rating/rating';
import Link from 'atomic/link/link';
import Icon from 'atomic/icon/icon';
import DsaLink from 'atomic/dsa-link/dsa-link';
import Tooltip from 'atomic/tooltip';

import './review-item.desktop.scss';

import credibleReviewIcon from 'assets/images/company-view/credible-review.inline.svg';

dayjs.locale('pl');

const ReviewItemDesktop: React.FC<ReviewItemProps> = ({ review, company, type }): ReactElement => {
    const { id, author, rating, content, createdAt } = review;

    const deliveryReview = review.deliveryReview
        ? (
            <ReviewItemMessage
                id={review.deliveryReview.id}
                type="delivery"
                content={review.deliveryReview.content}
                createdAt={review.deliveryReview.createdAt}
                author={review.deliveryReview.author}
            />
        )
        : null;

    const comment = review.comment
        ? (
            <ReviewItemMessage
                id={review.comment.id}
                type="comment"
                content={review.comment.content}
                createdAt={review.comment.createdAt}
                author={{ name: company.name }}
            />
        )
        : null;

    const userComment = review.userComment
        ? review.userComment.map(userCommentItem => (
            <ReviewItemMessage
                key={userCommentItem.id}
                id={userCommentItem.id}
                type="comment"
                author={userCommentItem.author}
                content={userCommentItem.content}
                createdAt={userCommentItem.createdAt}
            />
        ))
        : null;

    const addUserComment = type === 'product' && <ReviewAddComment reviewId={review.id} />;

    const opiconnectBox = review.dialogue || review.withdrawnReviewId
        ? (
            <ReviewItemOpiconnect
                status={review.active}
                isRelatedToWithdrawnReview={Boolean(review.withdrawnReviewId)}
                dialogue={review.dialogue}
                companyName={company.name}
                userName={review.author}
            />
        )
        : null;

    return (
        <li className="review-item">
            <div className="review-item__content">
                <div className="review-item__avatar review-item__avatar-main">
                    <Icon icon="avatar" className="review-item__avatar-icon" />
                </div>
                <div className="review-item__content-container">
                    <div className="review-item__content-columns">
                        <div className="review-item__content-left">
                            <div className="review-item__author">
                                {review.active === ReviewActiveType.active ? author : 'Użytkownik Opineo'}
                            </div>
                            <div className="review-item__rating">
                                <Rating value={rating} readOnly={true} />
                            </div>
                        </div>
                        <div className="review-item__content-right">
                            <ReviewItemLikes
                                reviewId={review.id}
                                likes={review.likesCount}
                                dislikes={review.dislikesCount}
                                type={type}
                            />
                            <span className="review-item__date">
                                { dayjs(createdAt).format('D MMMM YYYY | HH:mm') }
                            </span>
                            <DsaLink iconOnly={true} className="review-item__link" url={`/${type === 'company' ? 'opinia' : 'produkt'}/${id}`} />
                            <Link rel="nofollow" href={`/${type === 'company' ? 'opinia' : 'produkt'}/${id}`} className="review-item__link">
                                <Icon icon="link" className="review-item__content-right-icon" />
                            </Link>
                            {review.isCredible && (
                                <div className="review-item__credible-wrapper">
                                    <Tooltip
                                        position="bottom-left"
                                        message={'Ta opinia pochodzi z systemu ankiet potransakcyjnych. Opinia została'
                                            + ' zweryfikowana i pochodzi od klienta, który dokonał zakupów w sklepie.'}
                                    >
                                        <img
                                            className="review-item__credible"
                                            src={credibleReviewIcon}
                                            alt="Wiarygodna opinia"
                                        />
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    </div>
                    {review.active === ReviewActiveType.active && (
                        <div className="review-item__text">
                            {
                                review.product
                                    ? <ReviewItemProductDesktop product={review.product} content={content} />
                                    : content
                            }
                        </div>
                    )}
                    {opiconnectBox}
                    {deliveryReview}
                    {comment}
                    {addUserComment}
                    {userComment}
                </div>
            </div>
        </li>
    );
};

export default ReviewItemDesktop;
